import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { PlusIcon } from '../icons';
import { NAV } from '../strings';
import { useAdvancedMode } from '../hooks/useAdvancedMode';
import { useCompany } from '../hooks/useCompany';
import { useInboxCount } from '../hooks/useInboxCount';
import { Button } from '../components/Button';
import { CountBadge } from '../components/Badge';
import { Sheet } from '../components/Sheet';
import { CompanySwitcher } from './CompanySwitcher';
import { StatusPill } from './StatusPill';
import { BOOKKEEPING_ICON, PRIMARY_NAV, bookkeepingNav } from './navItems';

export interface SideNavProps {
  /** 64 px icon rail (768–1023 px) with tooltips. */
  rail?: boolean | undefined;
  onAdd: () => void;
}

/**
 * Desktop left navigation (248 px) or the tablet icon rail (64 px). The rail
 * keeps a small visible label under every icon — tooltips never show on
 * touch tablets — and folds the advanced "Bokföring" group into a sheet.
 */
export function SideNav({ rail = false, onAdd }: SideNavProps) {
  const { company, currentFiscalYear } = useCompany();
  const { advanced } = useAdvancedMode();
  const inboxCount = useInboxCount(company?.id);
  const BookIcon = BOOKKEEPING_ICON;
  const bookkeepingItems = bookkeepingNav(currentFiscalYear?.id);

  return (
    <nav className={rail ? 'side-nav side-nav-rail' : 'side-nav'} aria-label="Huvudmeny">
      <div className="side-nav-top">
        {/* The rail has room for the monogram only; the full sidebar shows the
            lockup, which carries the wordmark in its own typeface. */}
        <div className="side-nav-brand">
          <img src={rail ? '/favicon.svg' : '/logo.svg'} alt="EgenBalans" className={rail ? 'side-nav-logo' : 'side-nav-lockup'} width={rail ? 28 : undefined} height={rail ? 28 : undefined} />
        </div>
        {rail ? (
          <Button variant="primary" icon={PlusIcon} aria-label={NAV.add} onClick={onAdd} className="side-nav-add" data-tooltip={NAV.add} />
        ) : (
          <Button variant="primary" icon={PlusIcon} onClick={onAdd} className="side-nav-add" full>
            {NAV.add}
          </Button>
        )}
      </div>
      <ul className="side-nav-list">
        {PRIMARY_NAV.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.to}>
              <NavLink to={item.to} end={item.end ?? false} className={({ isActive }) => (isActive ? 'side-nav-link side-nav-link-active' : 'side-nav-link')}>
                <Icon size={22} />
                <span className="side-nav-label">{item.label}</span>
                {item.badge === 'inbox' ? <CountBadge count={inboxCount} label="att göra" /> : null}
              </NavLink>
            </li>
          );
        })}
      </ul>
      {advanced && rail ? (
        <RailBookkeepingMenu items={bookkeepingItems} />
      ) : advanced ? (
        <div className="side-nav-group" aria-label={NAV.bookkeeping}>
          <div className="side-nav-group-title">
            <BookIcon size={16} />
            <span>{NAV.bookkeeping}</span>
          </div>
          <ul className="side-nav-list side-nav-sublist">
            {bookkeepingItems.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} className={({ isActive }) => (isActive ? 'side-nav-link side-nav-sublink side-nav-link-active' : 'side-nav-link side-nav-sublink')}>
                  <span className="side-nav-label">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="side-nav-footer">
        <CompanySwitcher compact={rail} />
        <StatusPill kind="drive" compact={rail} />
        <StatusPill kind="ai" compact={rail} />
      </div>
    </nav>
  );
}

/**
 * Rail version of the advanced "Bokföring" group: a labelled icon button that
 * opens a sheet with the Layer-2 links, so touch tablets (no hover, no
 * tooltips) can still reach every bookkeeping page from the nav.
 */
function RailBookkeepingMenu({ items }: { items: readonly { to: string; label: string }[] }) {
  const [open, setOpen] = useState(false);
  const BookIcon = BOOKKEEPING_ICON;
  return (
    <div className="side-nav-group">
      <button type="button" className="side-nav-link side-nav-group-button" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
        <BookIcon size={22} />
        <span className="side-nav-label">{NAV.bookkeeping}</span>
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title={NAV.bookkeeping}>
        <ul className="side-nav-sheet-list">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} className={({ isActive }) => (isActive ? 'side-nav-sheet-link side-nav-sheet-link-active' : 'side-nav-sheet-link')} onClick={() => setOpen(false)}>
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </Sheet>
    </div>
  );
}
